import { Feedback } from "@views/components/Feedback/Feedback";
import { find, get } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useEnableScroll from "../../../custom-hooks/useEnableScroll";
import useFeatureTags from "../../../custom-hooks/useFeatureTags";
import { ChatBoxV2 } from "./ChatBox";
import { UploadFileOld } from "./UploadFile";
import NodeWrapper from "./components/NodeWrapper";
import { NO_WHEEL } from "./constants";
import { useCreateBlobUrl } from "./hooks/useCreateBlobUrl";
import { useRemoveConnect } from "./hooks/useRemoveConnect";
import { initialReportSizeMapping, nodeConfig } from "./nodeConfig";
import { NodesProps } from "./typing";
import { NodeConfigUtil } from "./NodeConfigUtil";
import { dislikePrint } from "@uiview/views/Nodes/BluePrintNode/BluePrintUtil";
import func from "@uikit/func";
import { useReactFlow } from "reactflow";
import styles from "./Nodes.module.scss";
import { TryResult, tryPromise } from "imagica-corekit/dist/base/cutil/LangUtil";
import isBlank from "@sedan-utils/is-blank";

export const Nodes: React.FC<NodesProps> = React.memo(props => {
  // Need it to debugger
  // console.log('Nodes');
  const feature_tags = useFeatureTags();
  const disableAddPreview = useSelector((state: any) => state.fot.disableAddPreview);

  const checkNodeArr: string[] = useSelector(state => get(state, "fot.checkNodeArr", []));
  const {
    displayType,
    isInCanvas = true,
    resizeEventParams,
    isOpenDeleteBox,
    id,
    showFeedback,
    isTargetNode,
    inputTitle,
    reason,
    allowFitSize,
  } = props;

  useRemoveConnect({ id, displayType });

  const createBlobUrl = useCreateBlobUrl(
    get(props, "textAreaValue.needToCreateBlobUrl"),
    get(props, "textAreaValue.unStructuredDownloadFailedText"),
    get(props, "initCreateBlobUrl"),
    get(props, "displayType")
  );

  const { enableScroll, mouseEnterLeave } = useEnableScroll();
  const { getEdges, getNodes } = useReactFlow();

  const {
    component,
    disableFeatureTag,
    initialReportSize = { height: 0, width: 0 },
  } = useMemo(() => {
    const config = find(nodeConfig, item => item.displayType === displayType) || {
      component: () => <div />,
      disableFeatureTag: "",
      displayType: "",
    };

    if (config.displayType === "chatBox" && feature_tags.enableStudiosCustomJsFunctionChatBox) {
      config.component = ChatBoxV2;
    }

    if (config.displayType === "uploadFile" && feature_tags.disableUploadFileV2) {
      config.component = UploadFileOld;
    }
    return { ...config, initialReportSize: initialReportSizeMapping[displayType] };
  }, [displayType]);

  const noWheelClassName = checkNodeArr?.includes(props.id) && enableScroll ? NO_WHEEL : "";

  const [likeActive, setLikeActive] = useState(props.feedback?.user_input == 1);
  const [dislikeActive, setDislikeActive] = useState(props.feedback?.user_input == -1);
  const [likeDisabled, setLikeDisabled] = useState(props.feedback?.user_input == -1);
  const [dislikeDisabled, setDislikeDisabled] = useState(props.feedback?.user_input == 1);

  const handleLike = () => {
    if (!likeActive) {
      //like
      props.updateFeedbackLike();
      setDislikeDisabled(true);
    } else {
      //cancel like
      props.updateFeedbackLikeCancel();
      setDislikeDisabled(false);
    }
    setLikeActive(!likeActive);
  };
  const handleDislike = () => {
    if (!dislikeActive) {
      //dislike
      props.updateFeedbackDislike();
      setLikeDisabled(true);
    } else {
      //cancel dislike
      props.updateFeedbackDislikeCancel();
      setLikeDisabled(false);
    }
    setDislikeActive(!dislikeActive);
  };

  const handleBluePrintDislike = useCallback(
    async (feedback: string) => {
      const nodes = getNodes();
      const edges = getEdges();
      const line = edges.find(x => x.target == props.id);
      const edgeNode = nodes.find(x => x.id === line?.source);
      const blueJson = JSON.parse(get(edgeNode, "data.lineParam.blueJson", JSON.stringify({})));
      const id = blueJson.id ?? blueJson.name ?? "recipe_maker";
      const output = get(props, "textAreaValue");
      const inputs = NodeConfigUtil.getInputsByTargetId(nodes, edges, props.id);
      const res = await tryPromise(dislikePrint({ id, feedback, inputs, output }));
      isBlank(res.error) ? func.messageUtil("Dislike successfully", "success") : func.messageUtil("Dislike Failed");
      return res;
    },
    [props.id, props.textAreaValue]
  );

  const enableFeedback =
    feature_tags.showUserFeedback && isTargetNode && showFeedback && !(createBlobUrl?.loading ?? false);

  const style = NodeConfigUtil.getNodeStyle(
    props.displayType,
    enableFeedback,
    allowFitSize,
    props.isInBlueprint,
    props.fromList,
    resizeEventParams,
    props.parentNodeId
  );

  //  打开add to preview 按钮 修改chat interface底部padding值，用于显示按钮
  const chatInterfaceStyle = props.displayType === "chatInterface" && !disableAddPreview ? { paddingBottom: 80 } : {};
  const isBluePrintNode = props.displayType === "blueprint";

  return (
    <NodeWrapper
      id={id}
      isInCanvas={isInCanvas}
      loading={createBlobUrl?.loading}
      displayType={props.displayType}
      isOpenDeleteBox={isOpenDeleteBox}
      mouseEnterLeave={mouseEnterLeave}
      style={{ ...style, ...chatInterfaceStyle }}
      reason={reason}
      inputTitle={inputTitle}
      isInBlueprint={props.isInBlueprint}
      enableFeedback={enableFeedback}
      typeInputRef={props.typeInputRef}
    >
      {React.createElement(component, {
        noWheelClassName,
        initialReportSize,
        enableFeedback,
        createBlobUrl,
        ...props,
        isInCanvas,
        isRootNode: displayType === "blueprint",
      })}

      {/* user feedback */}
      <div className={enableFeedback ? styles.feedback : ""}>
        {enableFeedback ? (
          <Feedback
            class={"singleNode"}
            id={props.id}
            likeDisabled={likeDisabled}
            dislikeDisabled={dislikeDisabled}
            likeActive={likeActive}
            dislikeActive={dislikeActive}
            handleLike={handleLike}
            handleDislike={handleDislike}
            isBluePrintParentNode={isBluePrintNode}
            handleBluePrintDislike={handleBluePrintDislike}
          />
        ) : null}
      </div>
    </NodeWrapper>
  );
});
